/**
 * NBDY product fragrance — size pills (variant picker).
 *
 * Mirrors the prototype's 5ml / 50ml pill switcher onto Shopify variants.
 * Updates the product form's hidden `id` input, the displayed price, and
 * the add-to-cart button's enabled state. Form submission and cart-drawer
 * integration are handled by Horizon's <product-form-component>.
 */
class NbdyFragrancePills extends HTMLElement {
  connectedCallback() {
    this.pills = Array.from(this.querySelectorAll('.nbdy-product-fragrance__pill'));
    if (this.pills.length === 0) return;

    const formId = this.dataset.form;
    this.form = formId ? document.getElementById(formId) : null;
    this.variantInput = this.form?.querySelector('input[name="id"]') ?? null;

    const root = this.closest('.shopify-section') || document;
    this.priceEl = root.querySelector('[data-fragrance-price]');
    this.compareEl = root.querySelector('[data-fragrance-compare]');
    this.discountEl = root.querySelector('[data-fragrance-discount]');
    this.priceRowEl = root.querySelector('[data-fragrance-price-row]');
    this.memberPriceEl = root.querySelector('[data-fragrance-member-price-value]');
    this.addButton = root.querySelector('[ref="addToCartButton"]');
    // The price now lives in its own row above the button. We swap the current
    // price to the sold-out string when a variant is unavailable, and restore
    // the variant's price (from pill.dataset.variantPrice) when back in stock.
    this.soldOutLabel = this.priceEl?.dataset.soldOut || 'Sold out';
    // CTA label + its "Add to cart" text — swapped to "Sold out" on a sold-out
    // variant (the cart animator only touches the label during add-to-cart,
    // which can't happen while the button is disabled).
    this.ctaLabelEl = root.querySelector('[data-cta-label]');
    this.addLabel = root.querySelector('nbdy-cta-animator')?.dataset.addLabel || 'Add to cart';

    this.addEventListener('click', this.#handleClick);

    // Member pricing is NATIVE now (BSS retired). The server already rendered the
    // member price for logged-in customers; the handler below just keeps it in
    // sync on a variant switch. `member-active` (logged in AND a discount %) and
    // `member-pct` are emitted on this element by the section.
    this.memberActive = this.dataset.memberActive === 'true';
    this.memberPct = this.dataset.memberPct || '';
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    const pill = event.target.closest('.nbdy-product-fragrance__pill');
    if (!pill || pill.disabled) return;

    const variantId = pill.dataset.variantId;
    if (!variantId) return;

    this.pills.forEach((p) => {
      const isActive = p === pill;
      p.classList.toggle('nbdy-product-fragrance__pill--active', isActive);
      p.setAttribute('aria-pressed', String(isActive));
    });

    if (this.variantInput) {
      this.variantInput.value = variantId;
    }

    const available = pill.dataset.variantAvailable === 'true';
    const memberPrice = pill.dataset.variantMemberPrice;
    const regularPrice = pill.dataset.variantPrice;

    // Main price: sold out shows the regular price (dimmed via the --sold-out
    // row class); logged-in member → member price; else regular.
    if (this.priceEl) {
      if (available && this.memberActive && memberPrice) {
        this.priceEl.textContent = memberPrice;
      } else if (regularPrice) {
        this.priceEl.textContent = regularPrice;
      }
    }

    // Sold-out: dim the price row + swap the CTA label to "Sold out".
    if (this.priceRowEl) {
      this.priceRowEl.classList.toggle('nbdy-product-fragrance__price-row--sold-out', !available);
    }
    if (this.ctaLabelEl) {
      this.ctaLabelEl.textContent = available ? this.addLabel : this.soldOutLabel;
    }

    // "Sale" state: the member discount takes precedence (struck regular + a
    // "Save X · Y% off" badge); otherwise fall back to a native compare-at sale.
    const memberOnSale = available && this.memberActive && memberPrice;
    const nativeOnSale = available && pill.dataset.variantOnSale === 'true';
    const onSale = memberOnSale || nativeOnSale;

    if (this.compareEl) {
      if (memberOnSale) this.compareEl.textContent = regularPrice || '';
      else if (nativeOnSale) this.compareEl.textContent = pill.dataset.variantComparePrice || '';
      else this.compareEl.textContent = '';
    }
    if (this.discountEl) {
      if (memberOnSale && pill.dataset.variantMemberSavings && this.memberPct) {
        this.discountEl.textContent = `Save ${pill.dataset.variantMemberSavings} · ${this.memberPct}% off`;
      } else if (nativeOnSale && pill.dataset.variantSavings && pill.dataset.variantDiscount) {
        this.discountEl.textContent = `Save ${pill.dataset.variantSavings} · ${pill.dataset.variantDiscount}% off`;
      } else {
        this.discountEl.textContent = '';
      }
    }
    if (this.priceRowEl) {
      this.priceRowEl.dataset.onSale = onSale ? 'true' : 'false';
    }

    // Signed-out teaser ("Sign in to purchase @ <member price>") — keep its price
    // in sync. Only present in the DOM when logged out.
    if (this.memberPriceEl && memberPrice) {
      this.memberPriceEl.textContent = memberPrice;
    }

    if (this.addButton) {
      this.addButton.disabled = !available;
    }
  };
}

if (!customElements.get('nbdy-fragrance-pills')) {
  customElements.define('nbdy-fragrance-pills', NbdyFragrancePills);
}

/**
 * NBDY fragrance tier — stagger reveal for the tier_strips notes layout.
 *
 * Each `<nbdy-fragrance-tier>` watches itself with an IntersectionObserver.
 * On first viewport entry: fade-zoom the mood image, fade-up the caption,
 * then stagger-fade-up the note chips (60 ms per chip).
 *
 * Uses the Web Animations API (no dependency). If you later want richer
 * easing / spring physics / sequencing primitives, swap the three
 * `element.animate(...)` calls in `#reveal()` for `anime.animate(...)` from
 * anime.js v4 — same API shape, drop-in.
 */
const REVEAL_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const prefersReducedMotion = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

class NbdyFragranceTier extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    if (this.dataset.revealed) return;

    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      this.dataset.revealed = 'instant';
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#reveal();
            this.#observer?.disconnect();
            this.#observer = null;
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: '0px 0px -10% 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #reveal() {
    this.dataset.revealed = 'animated';

    const animate = (el, keyframes, options) => {
      if (!el || typeof el.animate !== 'function') return;
      el.animate(keyframes, { ...options, fill: 'forwards' });
    };

    animate(
      this.querySelector('.nbdy-pf-tier__img'),
      [
        { opacity: 0, transform: 'scale(1.04)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      { duration: 800, easing: REVEAL_EASING }
    );

    animate(
      this.querySelector('.nbdy-pf-tier__caption'),
      [
        { opacity: 0, transform: 'translateY(12px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 600, delay: 120, easing: REVEAL_EASING }
    );

    const chips = this.querySelectorAll('.nbdy-pf-tier__chip');
    chips.forEach((chip, i) => {
      animate(
        chip,
        [
          { opacity: 0, transform: 'translateY(16px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 500, delay: 260 + i * 60, easing: REVEAL_EASING }
      );
    });
  }
}

if (!customElements.get('nbdy-fragrance-tier')) {
  customElements.define('nbdy-fragrance-tier', NbdyFragranceTier);
}

/**
 * NBDY fragrance bottle — levitating / ascended animation.
 *
 * On connect: ascends 40 px into position with a soft fade-up (~900 ms,
 * ease-out), then hands off to a perpetual gentle bob (±8 px Y, 3 s,
 * sine ease-in-out, alternate). All animation runs through `transform`
 * + `opacity` only (GPU-composited, no layout thrash).
 *
 * Driven by anime.js v4 (vendored at `assets/anime.esm.min.js`). We use
 * dynamic import so the bottle ascent/bob is fully optional — if the JS
 * import ever fails, the CSS reduced-motion fallback shows the bottle
 * settled in place with no animation.
 *
 * Lifecycle:
 *   - IntersectionObserver pauses the bob loop when the bottle scrolls
 *     off-screen and resumes it on re-entry (saves CPU/battery during the
 *     long tier-strips scroll).
 *   - disconnectedCallback aborts everything (no leaks if the section is
 *     re-rendered by the theme editor).
 *   - prefers-reduced-motion users skip the ascent and bob entirely; the
 *     CSS already shows the bottle settled (see the @media rule in the
 *     section's stylesheet).
 */
class NbdyFragranceBottle extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {any} */ #bobLoop = null;
  /** @type {boolean} */ #ascended = false;
  /** @type {AbortController} */ #abort = new AbortController();

  connectedCallback() {
    if (this.dataset.levitateState) return; // already initialized
    if (!('matchMedia' in window)) {
      this.dataset.levitateState = 'settled';
      return;
    }
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.dataset.levitateState = 'settled';
      return;
    }

    // anime.js URL is emitted as a data attr by the section's Liquid
    // (uses Shopify's `asset_url` filter so the versioned CDN path is correct).
    const animeUrl = this.dataset.animeUrl;
    if (!animeUrl) {
      this.dataset.levitateState = 'settled';
      return;
    }

    // Lazy-import so a failed fetch doesn't break the section.
    import(/* @vite-ignore */ animeUrl)
      .then((anime) => this.#start(anime))
      .catch(() => {
        // Drop the pre-ascent CSS state so the bottle is at least visible.
        this.dataset.levitateState = 'settled';
      });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#bobLoop?.pause?.();
    this.#bobLoop = null;
  }

  /**
   * @param {{ animate: Function, createSpring: Function }} anime
   */
  #start(anime) {
    if (this.#abort.signal.aborted) return;

    this.dataset.levitateState = 'ascending';

    // Ascent: pre-state (opacity 0, translateY 40px) is in CSS. Animate to
    // resting position via spring physics for a tactile "settling" feel.
    anime.animate(this, {
      opacity: [0, 1],
      translateY: [40, 0],
      duration: 900,
      ease: 'out(3)', // anime.js v4 'out' easing, gentle deceleration
      onComplete: () => {
        if (this.#abort.signal.aborted) return;
        this.dataset.levitateState = 'bobbing';
        this.#startBob(anime);
        this.#observeViewport();
      },
    });
  }

  /**
   * @param {{ animate: Function }} anime
   */
  #startBob(anime) {
    // Continuous Y-bob: ±8px, 3s, sine ease, alternating forever.
    // We anchor at translateY(0) on each loop iteration so the value stays
    // bounded (no drift across thousands of cycles).
    this.#bobLoop = anime.animate(this, {
      translateY: [
        { to: -8, duration: 1500, ease: 'inOut(2)' },
        { to: 0,  duration: 1500, ease: 'inOut(2)' },
      ],
      loop: true,
    });
  }

  #observeViewport() {
    if (!('IntersectionObserver' in window) || this.#abort.signal.aborted) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!this.#bobLoop) return;
          if (entry.isIntersecting) {
            this.#bobLoop.play?.();
          } else {
            this.#bobLoop.pause?.();
          }
        }
      },
      { threshold: 0 }
    );
    this.#observer.observe(this);
  }
}

if (!customElements.get('nbdy-fragrance-bottle')) {
  customElements.define('nbdy-fragrance-bottle', NbdyFragranceBottle);
}

/**
 * NBDY CTA Animator — three-state add-to-cart button.
 *
 *   ready  ─submit──▶  loading (icon slot opens, spinner spins)
 *          ◀─cart:error─                                ─cart:update─▶  added (check fades in, green flash, "Added!")
 *                                                       ─1.8s timeout─▶  ready (revert)
 *
 * Hooks:
 *   - listens for `submit` on the form (by id from data-form)
 *   - listens for `cart:update` on document (Horizon dispatches this after
 *     the cart POST succeeds; cart drawer auto-opens on the same event)
 *   - listens for `cart:error` on document (network/validation failure)
 *
 * Important: `expectingCartUpdate` flag scopes the animation to OUR submit —
 * `cart:update` fires for every cart change (drawer quantity edits, line
 * item removals, etc.), so without the flag the animation would trigger
 * on unrelated cart activity.
 *
 * We don't preventDefault on submit — Horizon's <product-form-component>
 * intercepts the form, POSTs to /cart/add.js, and dispatches cart:update
 * itself. We just observe the lifecycle.
 */
const CTA_RESET_DELAY_MS = 1800;
const CTA_REVEAL_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

class NbdyCtaAnimator extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLButtonElement | null} */
  #cta = null;
  /** @type {HTMLElement | null} */
  #labelEl = null;
  /** @type {SVGElement | null} */
  #checkEl = null;
  /** @type {string} */
  #addLabel = 'Add to cart';
  /** @type {string} */
  #addingLabel = 'Adding…';
  /** @type {string} */
  #addedLabel = 'Added to cart';
  /** @type {boolean} */
  #expectingCartUpdate = false;
  /** @type {number | null} */
  #resetTimer = null;

  connectedCallback() {
    const formId = this.dataset.form;
    this.#form = formId ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId)) : null;
    this.#cta = this.querySelector('.nbdy-product-fragrance__add');
    this.#labelEl = this.querySelector('[data-cta-label]');
    this.#checkEl = this.querySelector('[data-cta-check]');

    this.#addLabel = this.dataset.addLabel || this.#labelEl?.textContent?.trim() || 'Add to cart';
    this.#addingLabel = this.dataset.addingLabel || 'Adding…';
    this.#addedLabel = this.dataset.addedLabel || 'Added to cart';

    this.#form?.addEventListener('submit', this.#onSubmit);
    document.addEventListener('cart:update', this.#onCartUpdate);
    document.addEventListener('cart:error', this.#onCartError);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    document.removeEventListener('cart:error', this.#onCartError);
    if (this.#resetTimer) {
      clearTimeout(this.#resetTimer);
      this.#resetTimer = null;
    }
  }

  #onSubmit = () => {
    if (!this.#cta || this.#cta.disabled) return;
    this.#expectingCartUpdate = true;
    this.#setLoading();
  };

  #onCartUpdate = () => {
    // Ignore cart events that weren't caused by OUR submit (e.g. drawer
    // quantity changes, line removals, other product-form posts).
    if (!this.#expectingCartUpdate) return;
    this.#expectingCartUpdate = false;
    this.#setAdded();

    if (this.#resetTimer) clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => this.#setReady(), CTA_RESET_DELAY_MS);
  };

  #onCartError = () => {
    if (!this.#expectingCartUpdate) return;
    this.#expectingCartUpdate = false;
    this.#setReady();
  };

  #setReady() {
    this.#cta?.classList.remove('is-loading', 'is-added');
    if (this.#cta) this.#cta.disabled = false;
    if (this.#labelEl) this.#labelEl.textContent = this.#addLabel;
  }

  #setLoading() {
    this.#cta?.classList.add('is-loading');
    this.#cta?.classList.remove('is-added');
    if (this.#cta) this.#cta.disabled = true;
    if (this.#labelEl) this.#labelEl.textContent = this.#addingLabel;
  }

  #setAdded() {
    this.#cta?.classList.remove('is-loading');
    this.#cta?.classList.add('is-added');
    if (this.#cta) this.#cta.disabled = true;
    if (this.#labelEl) this.#labelEl.textContent = this.#addedLabel;

    // Tactile reveals — checkmark scale-in + label slide-up.
    if (this.#checkEl && typeof this.#checkEl.animate === 'function') {
      this.#checkEl.animate(
        [
          { opacity: 0, transform: 'scale(0.6)' },
          { opacity: 1, transform: 'scale(1)' },
        ],
        { duration: 320, easing: CTA_REVEAL_EASING, fill: 'forwards' }
      );
    }
    if (this.#labelEl && typeof this.#labelEl.animate === 'function') {
      this.#labelEl.animate(
        [
          { opacity: 0, transform: 'translateY(6px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 280, easing: CTA_REVEAL_EASING, fill: 'forwards' }
      );
    }
  }
}

if (!customElements.get('nbdy-cta-animator')) {
  customElements.define('nbdy-cta-animator', NbdyCtaAnimator);
}

/**
 * NBDY sticky guard — hides the fixed CTA bar before the reviews section.
 *
 * The bar (`.nbdy-product-fragrance__sticky`) is `position: fixed` on both
 * breakpoints, so without help it floats over everything below the section —
 * the Judge.me reviews and the footer. This headless controller (rendered as
 * a `hidden` sibling of the bar) watches the reviews section AND the footer
 * group with one IntersectionObserver whose bottom rootMargin is trimmed to
 * the bar's bottom edge: the moment either target reaches the bar, it sets
 * `data-suppressed="true"` on the bar and the CSS fades it out. Union
 * semantics (a Set of intersecting targets) so scrolling past a long reviews
 * list can't re-show the bar over the footer.
 *
 * The trim is re-measured (debounced) on resize / bar resize / theme-editor
 * section churn — this covers the 750px breakpoint flip where the bar swaps
 * between the desktop grid and the mobile pill. The CSS hides via
 * opacity+visibility only (never transform/display on the container), so
 * getBoundingClientRect() stays valid even while suppressed.
 *
 * No reviews section and no footer → observer is never created and the bar
 * behaves exactly as before. No IntersectionObserver support → same.
 */
const STICKY_SUPPRESS_BUFFER_PX = 24;
const STICKY_RESIZE_DEBOUNCE_MS = 150;

class NbdyStickyGuard extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {HTMLElement | null} */
  #sticky = null;
  /** @type {Set<Element>} */
  #intersecting = new Set();
  /** @type {number | null} */
  #debounceTimer = null;

  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;

    this.#sticky = this.closest('.shopify-section')?.querySelector('.nbdy-product-fragrance__sticky') ?? null;
    if (!this.#sticky) return;

    window.addEventListener('resize', this.#scheduleRebuild);
    // Theme editor: sections can be injected/removed/reordered live. Re-query
    // the boundary targets whenever that happens (no-ops on the storefront).
    document.addEventListener('shopify:section:load', this.#scheduleRebuild);
    document.addEventListener('shopify:section:unload', this.#scheduleRebuild);
    document.addEventListener('shopify:section:reorder', this.#scheduleRebuild);

    // Bar height varies per product (pill count, CTA label wrap) and can
    // change after fonts/labels settle — keep the trim in sync.
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleRebuild);
      this.#resizeObserver.observe(this.#sticky);
    }

    this.#rebuild();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
    }
    window.removeEventListener('resize', this.#scheduleRebuild);
    document.removeEventListener('shopify:section:load', this.#scheduleRebuild);
    document.removeEventListener('shopify:section:unload', this.#scheduleRebuild);
    document.removeEventListener('shopify:section:reorder', this.#scheduleRebuild);
    this.#intersecting.clear();
  }

  /**
   * Boundary elements the bar must never cover: the first reviews section
   * BELOW the fragrance section (one reordered above it must not suppress),
   * plus every footer-group section (fallback + long-reviews-list case).
   * Re-resolved on every rebuild so editor churn is always picked up.
   */
  #targets() {
    const section = this.closest('.shopify-section');
    const targets = [];

    if (section) {
      for (const el of document.querySelectorAll('.nbdy-reviews-section')) {
        if (section.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) {
          targets.push(el);
          break;
        }
      }
    }
    targets.push(...document.querySelectorAll('.shopify-section-group-footer-group'));
    return targets;
  }

  #scheduleRebuild = () => {
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = window.setTimeout(() => {
      this.#debounceTimer = null;
      this.#rebuild();
    }, STICKY_RESIZE_DEBOUNCE_MS);
  };

  #rebuild = () => {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#intersecting.clear();

    if (!this.#sticky) return;
    const rect = this.#sticky.getBoundingClientRect();
    if (rect.height === 0) return;

    const targets = this.#targets();
    if (targets.length === 0) {
      this.#sticky.dataset.suppressed = 'false';
      return;
    }

    // Trim the observation root's bottom edge up to the bar's BOTTOM edge
    // (where overlap actually begins) plus a small buffer so the fade
    // finishes before contact. Math.max guards safe-area/URL-bar geometry
    // where the bar bottom can sit at/past the viewport edge — worst case
    // the bar hides slightly early, never late.
    const trim = Math.max(0, Math.round(window.innerHeight - rect.bottom) + STICKY_SUPPRESS_BUFFER_PX);

    this.#observer = new IntersectionObserver(this.#onEntries, {
      root: null,
      threshold: 0,
      rootMargin: `0px 0px -${trim}px 0px`,
    });
    for (const target of targets) this.#observer.observe(target);
  };

  #onEntries = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) this.#intersecting.add(entry.target);
      else this.#intersecting.delete(entry.target);
    }
    if (this.#sticky) {
      this.#sticky.dataset.suppressed = this.#intersecting.size > 0 ? 'true' : 'false';
    }
  };
}

if (!customElements.get('nbdy-sticky-guard')) {
  customElements.define('nbdy-sticky-guard', NbdyStickyGuard);
}
